/* Phase 5 — /admin/policies — closed-deal ledger.
 * Styled to match /admin/leads. Admin/master sees all; agent sees own.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Filter, Download, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const GOLD = "#C9A84C";

const fmtCents = (c) =>
    c == null ? "—" : `$${(Number(c) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminPolicies() {
    const [params, setParams] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = ["master_admin", "admin"].includes(user?.role);

    const [filters, setFilters] = useState({
        carrier: params.get("carrier") || "",
        policy_type: params.get("policy_type") || "",
        agent_id: params.get("agent_id") || "",
        date_from: params.get("date_from") || "",
        date_to: params.get("date_to") || "",
        payment_status: params.get("payment_status") || "",
    });
    const [page, setPage] = useState(Number(params.get("page") || 1));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [carriers, setCarriers] = useState([]);
    const [policyTypes, setPolicyTypes] = useState([]);
    const [agents, setAgents] = useState([]);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const q = {
                page, page_size: 50,
                ...(filters.carrier ? { carrier: filters.carrier } : {}),
                ...(filters.policy_type ? { policy_type: filters.policy_type } : {}),
                ...(filters.agent_id && isAdmin ? { agent_id: filters.agent_id } : {}),
                ...(filters.date_from ? { date_from: filters.date_from } : {}),
                ...(filters.date_to ? { date_to: filters.date_to } : {}),
                ...(filters.payment_status ? { payment_status: filters.payment_status } : {}),
            };
            const r = await adminApi.policies(q);
            setData(r);
        } catch {
            toast.error("Couldn't load policies.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
         
    }, [filters, page]);

    useEffect(() => {
        adminApi.carriers({ active: true }).then(setCarriers).catch(() => {});
        adminApi.policyTypes({ active: true }).then(setPolicyTypes).catch(() => {});
        if (isAdmin) {
            adminApi.teamList({ role: "agent", page_size: 100 })
                .then((r) => setAgents(r.items || []))
                .catch(() => {});
        }
    }, [isAdmin]);

    useEffect(() => {
        const next = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => v && next.set(k, v));
        if (page !== 1) next.set("page", page);
        setParams(next, { replace: true });
         
    }, [filters, page]);

    const exportCsv = () => {
        const rows = data?.items || [];
        if (!rows.length) {
            toast.error("Nothing to export.");
            return;
        }
        const header = [
            "Date", "Lead", "Agent", "Carrier", "Policy Type",
            "Coverage", "Monthly Premium", "Annual Premium",
            "Effective Date", "Policy #",
        ];
        const lines = [header.join(",")];
        rows.forEach((p) => {
            const vals = [
                (p.created_at || "").slice(0, 10),
                csvSafe(p.lead_name),
                csvSafe(p.agent?.name || p.agent?.email || ""),
                csvSafe(p.carrier),
                csvSafe(p.policy_type),
                fmtCents(p.coverage_amount),
                fmtCents(p.monthly_premium),
                fmtCents(p.annual_premium),
                csvSafe(p.effective_date),
                csvSafe(p.policy_number || ""),
            ];
            lines.push(vals.join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `policies-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const totalAnnualShown = useMemo(() => {
        return (data?.items || []).reduce((sum, p) => sum + (p.annual_premium || 0), 0);
    }, [data]);

    return (
        <div className="space-y-5" data-testid="admin-policies">
            <header className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="mm-eyebrow">Closed Deals</div>
                    <h1 className="mm-display text-3xl text-mm-navy">
                        {isAdmin ? "Policies" : "My Policies"}
                    </h1>
                    {data?.total ? (
                        <p className="mt-1 text-xs text-mm-muted">
                            {data.total} total ·{" "}
                            <span className="font-semibold" style={{ color: GOLD }}>
                                {fmtCents(totalAnnualShown)} annualized on this page
                            </span>
                        </p>
                    ) : null}
                </div>
                <div className="flex items-center gap-2">
                    {isAdmin && (
                        <button
                            onClick={exportCsv}
                            className="mm-btn-outline !py-2 text-xs"
                            data-testid="policies-export-csv"
                        >
                            <Download size={12} /> Export CSV
                        </button>
                    )}
                    <div className="flex items-center gap-2 text-sm text-mm-muted">
                        <Filter size={14} />
                        Showing {data?.items?.length || 0} of {data?.total ?? 0}
                    </div>
                </div>
            </header>

            {/* Filters */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <SelectFilter
                    value={filters.carrier}
                    onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, carrier: v })); }}
                    options={carriers.map((c) => c.name)}
                    placeholder="All carriers"
                    testId="policies-filter-carrier"
                />
                <SelectFilter
                    value={filters.policy_type}
                    onChange={(v) => { setPage(1); setFilters((f) => ({ ...f, policy_type: v })); }}
                    options={policyTypes.map((t) => t.name)}
                    placeholder="All policy types"
                    testId="policies-filter-type"
                />
                {isAdmin && (
                    <Select
                        value={filters.agent_id || "__all__"}
                        onValueChange={(v) => {
                            setPage(1);
                            setFilters((f) => ({ ...f, agent_id: v === "__all__" ? "" : v }));
                        }}
                    >
                        <SelectTrigger data-testid="policies-filter-agent">
                            <SelectValue placeholder="All agents" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All agents</SelectItem>
                            {agents.map((a) => (
                                <SelectItem key={a.id} value={a.id}>
                                    {[a.first_name, a.last_name].filter(Boolean).join(" ") || a.email}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, date_from: e.target.value })); }}
                    placeholder="From"
                    data-testid="policies-filter-from"
                />
                <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, date_to: e.target.value })); }}
                    placeholder="To"
                    data-testid="policies-filter-to"
                />
            </div>

            {/* Phase 5.5 — Payment status chips */}
            <div className="flex flex-wrap items-center gap-2" data-testid="policies-payment-chips">
                <span className="text-[10px] uppercase tracking-wider text-mm-muted">Payment:</span>
                {[
                    { k: "", label: "All" },
                    { k: "pending", label: "Pending" },
                    { k: "paid", label: "Paid" },
                    { k: "missed", label: "Missed Draft", highlight: true },
                    { k: "lapsed", label: "Lapsed" },
                ].map((c) => {
                    const active = filters.payment_status === c.k;
                    return (
                        <button
                            key={c.k || "all"}
                            onClick={() => { setPage(1); setFilters((f) => ({ ...f, payment_status: c.k })); }}
                            className={[
                                "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
                                active
                                    ? (c.highlight ? "border-rose-400 bg-rose-600 text-white" : "border-mm-navy bg-mm-navy text-white")
                                    : (c.highlight ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100" : "border-mm-line bg-white text-mm-ink hover:bg-mm-bg"),
                            ].join(" ")}
                            data-testid={`policies-chip-${c.k || "all"}`}
                        >
                            {c.label}
                        </button>
                    );
                })}
            </div>

            {/* Table */}
            <div className="mm-card overflow-hidden p-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <Th>Date</Th>
                                <Th>Lead</Th>
                                {isAdmin && <Th>Agent</Th>}
                                <Th>Carrier</Th>
                                <Th>Policy Type</Th>
                                <Th className="text-right">Coverage</Th>
                                <Th className="text-right">Monthly</Th>
                                <Th className="text-right">Annual</Th>
                                <Th>Effective</Th>
                                <Th>Payment</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={isAdmin ? 10 : 9} className="py-10 text-center">
                                    <Loader2 className="mx-auto animate-spin text-mm-navy" />
                                </td></tr>
                            ) : (data?.items?.length || 0) === 0 ? (
                                <tr><td colSpan={isAdmin ? 10 : 9} className="py-10 text-center text-mm-muted">No closed deals match your filters.</td></tr>
                            ) : (
                                data.items.map((p) => (
                                    <tr
                                        key={p.id}
                                        onClick={() => setSelected(p)}
                                        className="cursor-pointer border-t border-mm-line transition-colors"
                                        style={{
                                            backgroundColor: `${GOLD}1A`, // 10% gold tint
                                        }}
                                        data-testid={`policies-row-${p.id}`}
                                    >
                                        <Td className="text-mm-ink">{(p.created_at || "").slice(0, 10)}</Td>
                                        <Td className="font-medium text-mm-navy">{p.lead_name || "—"}</Td>
                                        {isAdmin && <Td className="text-mm-ink">{p.agent?.name || "—"}</Td>}
                                        <Td>{p.carrier}</Td>
                                        <Td>{p.policy_type}</Td>
                                        <Td className="text-right tabular-nums">{fmtCents(p.coverage_amount)}</Td>
                                        <Td className="text-right tabular-nums font-medium" style={{ color: GOLD }}>
                                            {fmtCents(p.monthly_premium)}
                                        </Td>
                                        <Td className="text-right tabular-nums">{fmtCents(p.annual_premium)}</Td>
                                        <Td className="text-mm-muted">{p.effective_date || "—"}</Td>
                                        <Td><PaymentStatusPill status={p.payment_status} /></Td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && <PolicyDrawer policy={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}

function PolicyDrawer({ policy, onClose }) {
    return (
        <div className="fixed inset-0 z-40 flex" data-testid="policy-drawer">
            <div className="flex-1 bg-black/30" onClick={onClose} />
            <aside className="w-full max-w-md overflow-y-auto border-l border-mm-line bg-white p-6 shadow-2xl">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="mm-eyebrow">Closed deal</div>
                        <h2 className="mt-0.5 text-xl font-semibold text-mm-navy">
                            {policy.carrier} · {policy.policy_type}
                        </h2>
                        <div className="mt-0.5 text-xs text-mm-muted">
                            {policy.lead_name ? `Lead: ${policy.lead_name}` : "Unlinked lead"}
                            {policy.agent?.name ? ` · Agent: ${policy.agent.name}` : ""}
                        </div>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-mm-muted hover:bg-mm-bg">
                        <X size={14} />
                    </button>
                </div>
                <dl className="mt-5 space-y-3 text-sm">
                    <DLRow label="Coverage" value={fmtCents(policy.coverage_amount)} />
                    <DLRow label="Monthly premium" value={fmtCents(policy.monthly_premium)} highlight />
                    <DLRow label="Annual premium" value={fmtCents(policy.annual_premium)} highlight />
                    <DLRow label="Effective date" value={policy.effective_date || "—"} />
                    <DLRow label="Policy #" value={policy.policy_number || "—"} />
                    <DLRow label="Status" value={policy.status || "active"} />
                    <DLRow label="Created" value={(policy.created_at || "").slice(0, 16).replace("T", " ")} />
                </dl>
                {policy.notes && (
                    <div className="mt-5 rounded-xl border border-mm-line bg-mm-bg/60 p-3 text-xs text-mm-ink whitespace-pre-wrap">
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-mm-muted">Notes</div>
                        {policy.notes}
                    </div>
                )}
            </aside>
        </div>
    );
}

function DLRow({ label, value, highlight }) {
    return (
        <div className="flex items-center justify-between gap-4">
            <dt className="text-xs uppercase tracking-wider text-mm-muted">{label}</dt>
            <dd
                className={[
                    "text-right font-mono tabular-nums",
                    highlight ? "text-base font-semibold" : "text-sm",
                ].join(" ")}
                style={highlight ? { color: GOLD } : {}}
            >
                {value}
            </dd>
        </div>
    );
}

function SelectFilter({ value, onChange, options, placeholder, testId }) {
    return (
        <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid={testId}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="__all__">{placeholder}</SelectItem>
                {options.map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function Th({ children, className = "" }) {
    return <th className={`px-3 py-2.5 text-left font-medium ${className}`}>{children}</th>;
}
function Td({ children, className = "", style }) {
    return <td className={`px-3 py-2.5 align-middle ${className}`} style={style}>{children}</td>;
}

function csvSafe(s) {
    if (s == null) return "";
    const str = String(s);
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
}


const PAYMENT_STATUS_PILL = {
    pending: "bg-zinc-100 text-zinc-700 border-zinc-200",
    paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
    missed: "bg-rose-100 text-rose-700 border-rose-200",
    lapsed: "bg-rose-50 text-rose-700 border-rose-300 border-dashed",
};

function PaymentStatusPill({ status }) {
    const s = status || "pending";
    const label = s.charAt(0).toUpperCase() + s.slice(1);
    return (
        <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${PAYMENT_STATUS_PILL[s] || PAYMENT_STATUS_PILL.pending}`}
            data-testid={`payment-pill-${s}`}
        >
            {label}
        </span>
    );
}
